<template>
	<div class="flex w-full relative items-center">
		<Arrow
			v-if="showArrows"
			arrow-direction="left"
			:number-of-slides-to-show="blok.number_of_slides_to_show"
			:scroll-position="scrollPosition"
			:number-of-slides="slides.length"
			@click="slideLeft"
		/>

		<div
			ref="carouselRef"
			class="flex cursor-pointer overflow-x-auto w-full carousel pb-8"
			:class="aligning || 'items-center'"
			@mousedown="handleDragStart"
			@mouseup="handleDragEnd"
			@mouseleave="handleDragEnd"
			@mousemove="handleDrag"
		>
			<div
				v-for="slide in slides"
				:key="slide.Id ?? ('PartNo' in slide ? slide.PartNo : undefined)"
				:class="[
					`flex flex-col last:mr-0 first:ml-0 gap-4 text-center  mx-2 ${
						typeOfSlide === ItemType.CATEGORY
							? `carousel-category-slide ${categoriesSize}`
							: `carousel-product-slide ${wrapperSize}`
					}`
				]"
			>
				<NuxtLink
					v-if="typeOfSlide === ItemType.CATEGORY"
					:to="
						clickIsValid
							? localePath(
									`/${createCategoryLink({
										category: slide as CategoryWithFiles
									})}`
								)
							: ''
					"
				>
					<CategoryCard
						:category="slide as CategoryWithFiles"
						:lazy-loading="blok.lazy_load_images"
					/>
				</NuxtLink>
				<ProductCard
					v-if="typeOfSlide === ItemType.PRODUCT"
					:product="slide as ProductItem"
					:picture-size="pictureSize"
					:click-is-valid="clickIsValid"
					:lazy-loading="blok.lazy_load_images"
					:variants="
						(slide as ProductItem & { variants?: FilteredProductItem[] })
							.variants
					"
					:all-parametrics="allParametrics"
					:show-variants="showVariants"
					:show-add-to-cart="showAddToCart"
					@product-click="$emit('product-click', slide)"
				/>
			</div>
		</div>

		<Arrow
			v-if="showArrows"
			arrow-direction="right"
			:number-of-slides-to-show="blok.number_of_slides_to_show"
			:scroll-position="scrollPosition"
			:number-of-slides="slides.length"
			@click="slideRight"
		/>
	</div>
</template>

<script setup lang="ts">
// `carousel-product-slide` and `carousel-category-slide` are styling hooks
// (styled in sportkost and promix).
import { ref, computed, onMounted } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { CategoryWithFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type {
	ProductItem,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { BlokCarousel } from "~/types/storyblok/storyblok";
import { ItemType } from "~/utils/constants";

interface Props {
	blok: BlokCarousel;
	slides: CategoryWithFiles[] | ProductItem[];
	typeOfSlide: string;
	aligning?: string;
	/** Parametric value catalog — lets product cards render colour swatches. */
	allParametrics?: ParametricListValue[];
	/** Show variant (colour) swatches on product cards when slides carry them. */
	showVariants?: boolean;
	/**
	 * Render the product card's quick add-to-cart button. Only the templates
	 * whose `ProductCard` declares the prop react to it; for the rest it is an
	 * inert attribute, which is why this is opt-in rather than a default.
	 */
	showAddToCart?: boolean;
}

const {
	blok,
	slides,
	typeOfSlide,
	allParametrics,
	showVariants,
	// Explicit `undefined` default: Vue casts an absent Boolean prop to `false`,
	// which would pass `show-add-to-cart="false"` down to every product card on
	// every client rather than leaving the decision to the card.
	showAddToCart = undefined
} = defineProps<Props>();
defineEmits(["product-click"]);

const localePath = useLocalePath();
const { createCategoryLink } = useCategory();
const carouselRef = ref<HTMLElement>();
const isMouseDown = ref(false);
const clickIsValid = ref(true);
const mouseCoords = ref({
	startX: 0,
	scrollLeft: 0
});

const pictureSizes = {
	six: 199,
	four: 309
};

const showArrows = computed(() => {
	return +blok.number_of_slides_to_show < slides?.length && blok.show_arrows;
});

const scrollPosition = ref(0);
let cancelClick: ReturnType<typeof setTimeout>;
const handleDragStart = (e: MouseEvent) => {
	if (!carouselRef.value) return;
	isMouseDown.value = true;
	cancelClick = setTimeout(() => {
		clickIsValid.value = false;
	}, 200);
	carouselRef.value.classList.remove("scroll-smooth");

	const slider = carouselRef.value;
	const startX = e.pageX - slider.offsetLeft;
	const scrollLeft = slider.scrollLeft;
	mouseCoords.value = { startX, scrollLeft };

	carouselRef.value.style.cursor = "grabbing";

	e.preventDefault();
};
const handleDragEnd = () => {
	clearTimeout(cancelClick);
	clickIsValid.value = true;
	isMouseDown.value = false;
	if (!carouselRef.value) return;
	carouselRef.value.style.cursor = "pointer";
};
const handleDrag = (e: MouseEvent) => {
	if (!isMouseDown.value || !carouselRef.value) return;
	e.preventDefault();

	const x = e.pageX - carouselRef.value.offsetLeft;
	const walkX = (x - mouseCoords.value.startX) * 1.5;
	const scrollLeft = mouseCoords.value.scrollLeft - walkX;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};

// The distance between two slides, measured, so a template that resizes the
// slides in CSS still steps one card at a time. The size constants are the
// fallback (a single slide).
const getStepSize = (carousel: HTMLElement) => {
	const first = carousel.children[0] as HTMLElement | undefined;
	const second = carousel.children[1] as HTMLElement | undefined;
	if (first && second && second.offsetLeft > first.offsetLeft) {
		return second.offsetLeft - first.offsetLeft;
	}
	return blok.number_of_slides_to_show === "4"
		? pictureSizes.four * 1.065
		: pictureSizes.six * 1.065;
};

const slideBy = (direction: 1 | -1) => {
	const carousel = carouselRef.value;
	if (!carousel) return;
	if (direction < 0 && !carousel.scrollLeft) return;
	carousel.classList.add("scroll-smooth");
	const scrollLeft = carousel.scrollLeft + direction * getStepSize(carousel);
	carousel.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};
const slideRight = () => slideBy(1);
const slideLeft = () => slideBy(-1);
const pictureSize = ref<string>("");
const changePictureSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (blok.number_of_slides_to_show === "6") {
			if (
				carouselRef?.value &&
				carouselRef?.value?.offsetWidth >
					+blok.number_of_slides_to_show * pictureSizes.six
			) {
				return (pictureSize.value = `w-full h-[${pictureSizes.six}px]`);
			}
			return (pictureSize.value = `w-[${pictureSizes.six}px] h-[${pictureSizes.six}px]`);
		}
	} else if (blok.number_of_slides_to_show === "4") {
		return (pictureSize.value = `w-[${pictureSizes.four}px] h-[${pictureSizes.four}px]`);
	}
};
const categoriesSize = ref<string>("");
const changeCategoriesSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (blok.number_of_slides_to_show === "6") {
			if (
				carouselRef?.value &&
				carouselRef?.value?.offsetWidth > +blok.number_of_slides_to_show * 176
			) {
				return (categoriesSize.value = "w-2/12");
			}
			return (categoriesSize.value = "");
		}
	} else if (blok.number_of_slides_to_show === "4") {
		return (categoriesSize.value = "w-4/12 min-w-[176px]");
	}
};
const wrapperSize = ref<string>("");
const changeWrapperSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (
			carouselRef?.value &&
			carouselRef?.value?.offsetWidth >
				+blok.number_of_slides_to_show * pictureSizes.six
		) {
			return (wrapperSize.value = "w-2/12");
		}
		return (wrapperSize.value = "");
	} else if (blok.number_of_slides_to_show === "4") {
		return (wrapperSize.value = `w-[${pictureSizes.four}px]`);
	}
};

onMounted(async () => {
	changeWrapperSize();
	changePictureSize();
	changeCategoriesSize();
});
useResizeObserver(carouselRef, () => {
	changeWrapperSize();
	changePictureSize();
	changeCategoriesSize();
});
</script>
